import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type Props = {
  items: string[];
  value: string[];
  onChange: (ordered: string[]) => void;
  getDisplayLabel?: (item: string) => string;
};

export const TapToRank: React.FC<Props> = ({ 
  items, 
  value, 
  onChange, 
  getDisplayLabel = (item) => {
    // Format camelCase words by adding spaces before uppercase letters
    return item.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
  }
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [rankedItems, setRankedItems] = useState<string[]>([]);

  // Initialize ranked items - always start empty for tap-to-rank behavior
  useEffect(() => {
    // Only sync with external value if it's empty (external reset)
    if (value.length === 0) {
      setRankedItems([]);
    }
  }, [value]);

  const handleTap = (item: string) => {
    let newRankedItems: string[];
    
    if (rankedItems.includes(item)) {
      // Remove item from ranking
      newRankedItems = rankedItems.filter(i => i !== item);
    } else {
      // Add item to ranking (append to end)
      newRankedItems = [...rankedItems, item];
    }
    
    setRankedItems(newRankedItems);
    onChange(newRankedItems);
  };

  const getRank = (item: string) => {
    const index = rankedItems.indexOf(item);
    return index >= 0 ? index + 1 : null;
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = rankedItems.indexOf(String(active.id));
    const to = rankedItems.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    
    const newRankedItems = arrayMove(rankedItems, from, to);
    setRankedItems(newRankedItems);
    onChange(newRankedItems);
  };

  if (isMobile) {
    return (
      <Box maxWidth={720} mx="auto">
        <Typography 
          variant="body2" 
          color="text.secondary" 
          gutterBottom 
          textAlign="center"
          sx={{ mb: 2 }}
        >
          (1 = Highest, {items.length} = Lowest)
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item) => {
            const rank = getRank(item);
            return (
              <Box
                key={item}
                onClick={() => handleTap(item)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: rank 
                    ? '2px solid #E91E63' 
                    : '2px solid #e0e0e0',
                  backgroundColor: '#ffffff',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Typography 
                  variant="body1" 
                  fontWeight="medium"
                  sx={{ color: '#000' }}
                >
                  {getDisplayLabel(item)}
                </Typography>
                {rank ? (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: '#E91E63',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'white !important',
                        fontWeight: 'bold',
                        fontSize: '0.875rem',
                      }}
                    >
                      {rank}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: '2px solid #e0e0e0',
                      backgroundColor: '#ffffff',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Desktop version with drag and drop
  return (
    <Box maxWidth={720} mx="auto">
      <Typography variant="body2" color="text.secondary" gutterBottom textAlign="center">
        (1 = Highest, {rankedItems.length} = Lowest)
      </Typography>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={rankedItems} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rankedItems.map((item, index) => (
              <SortableRow 
                key={item} 
                id={item} 
                label={getDisplayLabel(item)} 
                rank={index + 1}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

function SortableRow({ id, label, rank }: { id: string; label: string; rank: number }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        p: 2,
        borderRadius: 2,
        cursor: 'grab',
        transition: transition,
        transform: CSS.Transform.toString(transform),
        opacity: isDragging ? 0.6 : 1,
        backgroundColor: '#ffffff',
        border: '2px solid #E91E63',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '&:active': { cursor: 'grabbing' },
        touchAction: 'none',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DragIndicator sx={{ color: '#E91E63', fontSize: 20 }} />
        <Typography variant="body1" fontWeight="medium" color="#000">
          {label}
        </Typography>
      </Box>
      
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: '#E91E63',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            color: 'white !important',
            fontWeight: 'bold',
            fontSize: '0.875rem',
          }}
        >
          {rank}
        </Typography>
      </Box>
    </Box>
  );
}
